import React, { useEffect, useState } from "react";
import { getStorage } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import {
    BackButton,
    List,
    ListItem,
    Page,
    Toolbar,
    ProgressCircular,
    Input,
    Button
} from "react-onsenui";
import { useDate } from "../useDate";
import { axiosInstance as axios } from "../utils/axios";
import { paths } from "../App";

export const ChecklistMyChecklists = (props) => {

    const [authUser, setAuthUser] = useState(getStorage('u4'));
    const dateRT = useDate();

    /* Navigation */
    let navigate = useNavigate();
    const goBack = () => navigate(paths.checklist.dash, { replace: true });

    const [blueprints, setBlueprints] = useState(null);
    const [filteredBlueprints, setFilteredBlueprints] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);

    // Fetch blueprints with locations for user's organization
    const fetchBlueprints = async () => {
        if (!authUser || !authUser?.hotel_id) return;
        
        setLoading(true);
        try {
            const response = await axios.get('/checklist/blueprint/list', {
                params: { org_id: authUser.hotel_id }
            });
            
            if (response && response.data) {
                // Get user's desk viewing rights
                const allowedDeskIds = (authUser?.desks_ids || [])
                    .map(id => parseInt(id))
                    .filter(id => !isNaN(id));

                // If no viewing rights, stop here
                if (!allowedDeskIds.length) {
                    setBlueprints([]);
                    setFilteredBlueprints([]);
                    return;
                }
                
                // Filter blueprints by user's desk access (desk_id)
                const filteredBlueprints = response.data.filter(bp => {
                    // Check if blueprint's desk_id is within viewing rights
                    return bp.desk_id && allowedDeskIds.includes(parseInt(bp.desk_id));
                });
                
                // Expand blueprints with their locations
                const expanded = [];
                filteredBlueprints.forEach(bp => {
                    if (bp.locations && bp.locations.length > 0) {
                        bp.locations.forEach(loc => {
                            expanded.push({
                                blueprint: bp,
                                location: loc,
                                blueprintId: bp.id,
                                locationId: loc.id,
                                blueprintName: bp.name,
                                locationName: loc.location,
                                orgId: bp.org_id || authUser.hotel_id
                            });
                        });
                    } else {
                        // Blueprint without locations - still show it but without location
                        expanded.push({
                            blueprint: bp,
                            location: null,
                            blueprintId: bp.id,
                            locationId: null,
                            blueprintName: bp.name,
                            locationName: 'No location',
                            orgId: bp.org_id || authUser.hotel_id
                        });
                    }
                });
                
                setBlueprints(expanded);
                setFilteredBlueprints(expanded);
            }
        } catch (error) {
            console.error("Error fetching blueprints:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBlueprints();
    }, [authUser]);

    // Filter blueprints based on search query
    useEffect(() => {
        if (!blueprints) return;
        
        if (!searchQuery.trim()) {
            setFilteredBlueprints(blueprints);
            return;
        }

        const query = searchQuery.toLowerCase().trim();
        const filtered = blueprints.filter(item => {
            const matchesBlueprint = item.blueprintName?.toLowerCase().includes(query);
            const matchesLocation = item.locationName?.toLowerCase().includes(query);
            return matchesBlueprint || matchesLocation;
        });
        
        setFilteredBlueprints(filtered);
    }, [searchQuery, blueprints]);

    const handleSelectLocation = (item) => {
        console.log("handleSelectLocation called with:", item);
        
        if (!item.locationId) {
            alert("This checklist template does not have a location assigned. Please contact your administrator.");
            return;
        }

        // Construct a URL hash similar to QR code format
        // The ChecklistSheet component expects a decodedText with a hash parameter
        const hashData = {
            id: item.blueprintId,
            geek: "@thinkdj",
            bpi: item.blueprintId,
            oi: item.orgId,
            loi: item.locationId,
            bpn: item.blueprintName,
            lon: item.locationName
        };

        let hashString = '';
        try {
            const serialized = JSON.stringify(hashData);
            hashString = window.btoa(unescape(encodeURIComponent(serialized)));
        } catch (error) {
            console.error("Error generating hash string:", error);
            alert("Unable to generate checklist link. Please contact your administrator.");
            return;
        }
        const decodedText = `https://atom.checkinn.co/r?hash=${hashString}`;

        console.log("Navigating with decodedText:", decodedText);

        // Navigate to ChecklistSheet with the data
        navigate(paths.checklist.details, {
            state: {
                data: {
                    decodedText: decodedText
                }
            }
        });
    };

    const renderSearchInput = () => (
        <div className="p-t-b-10">
            <Input
                modifier="material"
                placeholder="Search by template or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%' }}
            />
        </div>
    );

    const renderListFooter = () => <></>;

    return (
        <Page renderToolbar={() =>
            <Toolbar>
                <div className="left">
                    <BackButton onClick={goBack}>Back</BackButton>
                </div>
                <div className="center">
                    <small>{dateRT}</small>
                </div>
                <div className="right">
                    {/* RHS */}
                </div>
            </Toolbar>
        }>
            <div className="p-l-r-15 m-b-20 m-t-15">
                <h3 style={{ lineHeight: '1.32', borderLeft: '3px solid #1e88e5', paddingLeft: 12 }}>
                    <span>My Checklists</span>
                    <small className="opa-050 d-b f-s-094r">Select a location to create a checklist</small>
                </h3>

                {renderSearchInput()}

                {loading && (
                    <div className="flex flex-both-center" style={{ height: '100%', minHeight: '75vh' }}>
                        <span><ProgressCircular modifier="material" indeterminate={true} /></span>
                    </div>
                )}

                {!loading && (!filteredBlueprints || filteredBlueprints.length === 0) && (
                    <div className="flex flex-both-center" style={{ height: '100%', minHeight: '75vh' }}>
                        <span>
                            {searchQuery ? 'No checklists found matching your search' : 'No checklists available'}
                        </span>
                    </div>
                )}

                {!loading && filteredBlueprints && filteredBlueprints.length > 0 && (
                    <div>
                        <List
                            dataSource={filteredBlueprints}
                            renderFooter={renderListFooter}
                            renderRow={(item, i) => {
                                const handleClick = () => {
                                    if (item.locationId) {
                                        handleSelectLocation(item);
                                    }
                                };
                                
                                return (
                                    <ListItem
                                        key={`${item.blueprintId}-${item.locationId || 'no-loc'}-${i}`}
                                        modifier={(i === (filteredBlueprints?.length - 1)) ? 'longdivider' : null}
                                        style={{ 
                                            borderBottom: '1px solid #cdcdcd',
                                            opacity: item.locationId ? 1 : 0.6
                                        }}
                                        onClick={handleClick}
                                        tappable={!!item.locationId}
                                    >
                                        <div className="p-r-15" style={{ width: '100%' }}>
                                            <strong>{item.locationName || 'Unassigned location'}</strong>
                                            <small className="d-b f-s-09r p-t-10 col-grey-dark">
                                                Template: {item.blueprintName}
                                                {item.locationId ? (
                                                    <>
                                                        <br />
                                                        <span className="col-primary">Tap to create checklist</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <br />
                                                        <span className="col-grey">No location assigned</span>
                                                    </>
                                                )}
                                            </small>
                                        </div>
                                    </ListItem>
                                );
                            }}
                        />
                    </div>
                )}
            </div>
        </Page>
    );
};

